import Chat from "./chat";
import NavCancel from "./nav-cancel";
import { useEffect, useState, RefObject } from "react";
import PanelDismiss from "./panel-dismiss";
import { useUser, useUI, useChat, useNotifications } from "../contexts";
import type { ChatMessage, Chat as ChatType } from "../contexts/ChatMessageProvider";
import { MessageSubMenu, ChatSubMenu } from "./submenu";
import ChatUserMenu from "./chat-user-menu";

const ChatModal = ({ isOwnListing, id, companion }) => {
	const { profile } = useUser();
	const {
		chatOpen, setChatOpen, setChatMenuOpen,
		setChatUserMenuOpen
	} = useUI();
	const { setChatCompanion, findChatForListing, getMessages, markChatRead, clearOpenChat, chatCompanion } = useChat();
	const { fetchUnreadCounts } = useNotifications();
	const [activeMessage, setActiveMessage] = useState<ChatMessage | null>({} as ChatMessage);
	const [activeChat] = useState<ChatType>({} as ChatType);
	const [messageTriggerRef, setMessageTriggerRef] = useState<RefObject<HTMLDivElement | null> | undefined>(undefined);
	const [userMenuTriggerRef, setUserMenuTriggerRef] = useState<RefObject<HTMLDivElement | null> | undefined>(undefined);

	// Load chat data when modal opens
	useEffect(() => {
		if (chatOpen && id && profile?.id && companion?.id) {
			// The companion is you: the page passed a listing's seller while
			// you are that seller. No chat has you on both sides, so the
			// lookup can only miss, and the thread left open elsewhere must
			// not stay behind for a reply to land in.
			if (String(companion.id) === String(profile.id)) {
				clearOpenChat();
				return;
			}
			// Set companion data first (the check above saw its id).
			setChatCompanion(companion);

			// Use find endpoint to check for existing chat (more efficient than getAllChats)
			void findChatForListing(id, profile.id, companion.id)
				.then(async (response) => {
					if (!(response.success && response.chatId)) {
						// No live chat for this listing. The provider still holds
						// whatever thread was open last (My Chats, another
						// listing), and a message typed here would have gone into
						// it: to another person, or into a deleted chat.
						clearOpenChat();
						return;
					}
					// Reading the thread here reads it. Only My Chats used to
					// mark a chat read, so replies read in this window kept the
					// "New" badges up and came back in the unread-chat email.
					const chatId = response.chatId;
					if ((await getMessages(chatId)) && (await markChatRead(chatId))) {
						await fetchUnreadCounts();
					}
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatOpen, id, companion, profile?.id]);
	return (
		<PanelDismiss
			className="chat-modal-cont"
			side="Bottom"
			onChange={() => {
				void setChatOpen(false);
				setChatMenuOpen(false);
				setChatUserMenuOpen(false);
			}}
			handle="chat-modal-handle"
			expanded={chatOpen}
		>
			<div className="chat-modal" data-testid="chat-modal">
				<div className="chat-modal-inner same-color-2 same-color-border">
					<div
						className="chat-exit"
						data-testid="chat-close-button"
						onClick={() => setChatOpen(false)}
					>
						{NavCancel()}
					</div>
					<Chat
						id={id}
						isOwnListing={isOwnListing}
						activeMessage={activeMessage}
						setActiveMessage={setActiveMessage}
						onMessageTriggerRef={setMessageTriggerRef}
						onUserMenuTriggerRef={setUserMenuTriggerRef}
					/>
				</div>
				<ChatSubMenu activeChat={activeChat as ChatType & { me?: { id: string; name?: string } }} />
			</div>
			<MessageSubMenu
				activeMessage={activeMessage as unknown as { id: string; chatId: string; text: string }}
				setActiveMessage={setActiveMessage as unknown as (msg: Record<string, unknown>) => void}
				triggerRef={messageTriggerRef}
			/>
			<ChatUserMenu companion={chatCompanion || companion} triggerRef={userMenuTriggerRef} />
		</PanelDismiss>
	);
};
export default ChatModal;
